<ng-container>
  <div *ngIf="property$ | async as property">
    <!-- Initial Screen -->
    <ng-container *ngIf="stage === 'has_account'">
      <div>
        <h2 class="text-2xl font-semibold mb-6">Do you have an existing Booking.com account?</h2>
        <div class="flex flex-col gap-4">
          <button
            type="button"
            (click)="onHasAccountSelected(true)"
            class="text-left px-4 py-6 rounded-lg border border-gray-200 border-solid hover:bg-gray-50"
            [class.bg-gray-50]="hasExistingAccount === true">
            Yes, use my existing Booking.com account
          </button>
          <button
            type="button"
            (click)="onHasAccountSelected(false)"
            class="text-left px-4 py-6 rounded-lg border border-gray-200 border-solid hover:bg-gray-50"
            [class.bg-gray-50]="hasExistingAccount === false">
            No, create a new Booking.com account for me
          </button>

          <button
            type="button"
            (click)="onContinueWithLegalEntityId()"
            class="text-primary hover:underline text-sm mt-2 text-left font-medium">
            I want to continue with my legal entity ID
          </button>
        </div>
      </div>
    </ng-container>

    <!-- Existing Account Selection -->
    <ng-container *ngIf="stage === 'existing_account'">
      <div class="flex flex-col">
        <h2 class="text-2xl font-semibold mb-6">Select your Booking.com account</h2>

        @if (loadingEntities) {
          <mat-spinner diameter="24"></mat-spinner>
        } @else {
          <div class="flex flex-col gap-4">
            @if (legalEntities.length > 0) {
              <h3 class="text-lg font-medium mb-2">Choose from your existing accounts</h3>
              <div class="flex flex-col gap-4">
                @for (entity of legalEntities; track entity.legal_entity_id) {
                  <div
                    class="flex items-center gap-4 p-4 border rounded-lg cursor-pointer hover:bg-gray-50"
                    (click)="onExistingAccountSelected(entity)"
                    [class.border-primary]="selectedEntity?.legal_entity_id === entity.legal_entity_id">
                    <input
                      type="radio"
                      [checked]="selectedEntity?.legal_entity_id === entity.legal_entity_id"
                      class="h-4 w-4 cursor-pointer" />
                    <div class="flex flex-col">
                      <span class="font-bold">{{ entity.company_name ?? entity.legal_contact_name }}</span>
                      <span class="text-sm text-gray-600">{{ entity.login_email }}</span>
                    </div>
                  </div>
                }
              </div>
            } @else {
              <p class="text-smoke-darker">No existing Booking.com accounts found linked to this property.</p>
            }

            <!-- Add email check form back -->
            <div class="mt-2">
              <h3 class="text-lg font-medium mb-2">Or connect another existing account</h3>
              <form [formGroup]="emailCheckForm" (ngSubmit)="checkEmail()" class="flex flex-col gap-4">
                <div class="flex flex-col gap-2">
                  <label for="existing_email" class="text-sm font-medium">Booking.com account email</label>
                  <mat-form-field class="nopadding">
                    <input
                      matInput
                      type="email"
                      formControlName="email"
                      placeholder="jane.doe@example.com"
                      autocomplete="email" />
                  </mat-form-field>

                  @if (emailError) {
                    <span class="text-red-500 text-sm">{{ emailError }}</span>
                  }
                </div>
                <!-- Check Email button moved to footer -->
              </form>
            </div>
          </div>
        }
      </div>
    </ng-container>

    <!-- Enter Legal Entity ID and Email -->
    <ng-container *ngIf="stage === 'enter_legal_entity_id_and_email'">
      <div class="flex flex-col">
        <h2 class="text-2xl font-semibold mb-6">Enter Legal Entity ID and Email</h2>
        <p class="text-gray-600 mb-4">Provide your Booking.com Legal Entity ID and the associated account email.</p>
        <form
          [formGroup]="legalEntityIdAndEmailForm"
          (ngSubmit)="onSubmitLegalEntityIdAndEmail()"
          class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <label for="legalEntityId" class="text-sm font-medium">Legal Entity ID</label>
            <mat-form-field class="nopadding">
              <input
                matInput
                formControlName="legalEntityId"
                placeholder="Enter your Legal Entity ID (e.g., 1234567)"
                type="text"
                inputmode="numeric" />
              <!-- Use text/numeric for better pattern matching -->
            </mat-form-field>
            @if (
              legalEntityIdAndEmailForm.controls['legalEntityId'].invalid &&
              (legalEntityIdAndEmailForm.controls['legalEntityId'].dirty ||
                legalEntityIdAndEmailForm.controls['legalEntityId'].touched)
            ) {
              @if (legalEntityIdAndEmailForm.controls['legalEntityId'].errors?.['required']) {
                <span class="text-red-500 text-sm">Legal Entity ID is required.</span>
              }
              @if (legalEntityIdAndEmailForm.controls['legalEntityId'].errors?.['pattern']) {
                <span class="text-red-500 text-sm">Please enter only numbers for the Legal Entity ID.</span>
              }
            }
          </div>
          <div class="flex flex-col gap-2">
            <label for="legalEntityEmail" class="text-sm font-medium">Associated Booking.com Email</label>
            <mat-form-field class="nopadding">
              <input
                matInput
                formControlName="email"
                placeholder="john.doe@example.com"
                autocomplete="email"
                type="email" />
            </mat-form-field>
            @if (
              legalEntityIdAndEmailForm.controls['email'].invalid &&
              (legalEntityIdAndEmailForm.controls['email'].dirty || legalEntityIdAndEmailForm.controls['email'].touched)
            ) {
              <span class="text-red-500 text-sm">Please enter a valid email address.</span>
            }
          </div>
        </form>
      </div>
    </ng-container>

    <!-- New Account Email Input -->
    <ng-container *ngIf="stage === 'create_account_email'">
      <div class="flex flex-col">
        <h2 class="text-2xl font-semibold mb-6">Create a new account</h2>
        <p class="text-gray-600 mb-4">We'll use this email to create your new Booking.com account.</p>
        <form [formGroup]="newAccountEmailForm" (ngSubmit)="onSubmitEmail()" class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <mat-form-field class="nopadding">
              <input matInput formControlName="email" placeholder="john.doe@example.com" autocomplete="email" />
            </mat-form-field>

            @if (emailError) {
              <span class="text-red-500 text-sm">{{ emailError }}</span>
            }
          </div>
        </form>
      </div>
    </ng-container>

    <!-- Entity Type Selection -->
    <ng-container *ngIf="stage === 'entity_type'">
      <div class="flex flex-col">
        <h2 class="text-2xl font-semibold mb-6">Select Entity Type</h2>
        <div class="flex flex-col gap-4">
          <button
            type="button"
            class="text-left p-4 rounded-lg border border-gray-200 border-solid hover:bg-gray-50"
            (click)="entityType = 'individual'; onEntityTypeSelected()">
            <div class="flex flex-col">
              <span class="font-medium">Individual</span>
              <span class="text-sm text-gray-600">I am renting out my property as an individual</span>
            </div>
          </button>

          <button
            type="button"
            class="text-left p-4 rounded-lg border border-gray-200 border-solid hover:bg-gray-50"
            (click)="entityType = 'organization'; onEntityTypeSelected()">
            <div class="flex flex-col">
              <span class="font-medium">Company</span>
              <span class="text-sm text-gray-600">I am renting out my property as a company</span>
            </div>
          </button>
        </div>
      </div>
    </ng-container>

    <!-- Entity Details Form -->
    <ng-container *ngIf="stage === 'entity_details'">
      <div class="flex flex-col">
        <h2 class="text-2xl font-semibold mb-6">Enter Entity Details</h2>
        <form [formGroup]="legalEntityForm" (ngSubmit)="onSubmitCreateLegalEntity()" class="flex flex-col gap-4">
          @if (entityType === 'organization') {
            <div class="flex flex-col gap-2">
              <label for="company_name" class="text-sm font-medium">Company Name</label>
              <mat-form-field class="nopadding">
                <input matInput formControlName="company_name" placeholder="Enter your company name" />
              </mat-form-field>
            </div>
            <div class="flex flex-col gap-2">
              <label for="legal_name" class="text-sm font-medium">Legal Name</label>
              <mat-form-field class="nopadding">
                <input matInput formControlName="legal_name" placeholder="Enter your legal name" />
              </mat-form-field>
            </div>
          }

          <div class="flex flex-col gap-2">
            <label for="legal_contact_name" class="text-sm font-medium">Contact Name</label>
            <mat-form-field class="nopadding">
              <input matInput formControlName="legal_contact_name" placeholder="Enter contact name" />
            </mat-form-field>
          </div>

          <div class="flex flex-col gap-2">
            <label for="legal_contact_phone_number" class="text-sm font-medium">Phone Number</label>
            <mat-form-field class="nopadding">
              <input matInput formControlName="legal_contact_phone_number" placeholder="Enter phone number" />
            </mat-form-field>
          </div>

          <div class="flex flex-col gap-2">
            <label for="country" class="text-sm font-medium">Country</label>
            <select id="country" formControlName="country" class="p-2 border rounded-lg">
              <option value="">Select a country</option>
              @for (country of countries$ | async; track country.alpha2code) {
                <option [value]="country.alpha2code">{{ country.name }}</option>
              }
            </select>
          </div>

          <!-- Submit button moved to footer -->
          <!-- <sbnb-button type="primary" (click)="onSubmitCreateLegalEntity()" [disabled]="!legalEntityForm.valid || loading"> ... </sbnb-button> -->
        </form>
      </div>
    </ng-container>

    <!-- Ready to Sync -->
    <ng-container *ngIf="stage === 'ready_to_sync'">
      <div class="flex flex-col">
        <h2 class="text-2xl font-semibold mb-6">Ready to Sync</h2>
        <div class="flex flex-col gap-6">
          <!-- Property Details -->
          <div class="p-4 border rounded-lg bg-white shadow-sm">
            <div class="flex gap-4 items-center">
              @if (property.photos?.[0]?.xx_large) {
                <img
                  [src]="property.photos[0].xx_large"
                  alt="Property Thumbnail"
                  class="w-24 h-24 object-cover rounded-md border" />
              }
              <div class="flex flex-col">
                <p class="text-base m-0 font-semibold text-gray-900">{{ property.name }}</p>
                @if (property.address) {
                  <p class="text-sm m-0 text-gray-600">{{ property.address }}</p>
                }
              </div>
            </div>
          </div>

          <div class="text-5xl text-center">🤝</div>

          <!-- Account Details -->
          <div class="p-4 border rounded-lg bg-white shadow-sm">
            <div class="flex gap-4 items-center">
              <img
                src="assets/images/connected-accounts/booking.svg"
                alt="Booking.com Logo"
                class="w-24 h-auto object-contain" />
              @if (legalEntityIdAndEmailForm.get('legalEntityId')?.value) {
                <div>
                  <p class="text-base m-0 font-medium">Legal entity ID: {{ selectedEntity?.legal_entity_id }}</p>
                  <p class="text-base m-0 text-gray-600">Email: {{ selectedEntity?.login_email }}</p>
                </div>
              } @else {
                <div>
                  <p class="text-base m-0 font-medium">Company name: {{ selectedEntity.company_name }}</p>
                  <p class="text-base m-0 text-gray-600">Legal contact name: {{ selectedEntity.legal_contact_name }}</p>
                  <p class="text-base m-0 text-gray-600">Email: {{ selectedEntity.login_email }}</p>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    </ng-container>

    <!-- Syncing State -->
    <ng-container *ngIf="stage === 'syncing'">
      <div class="flex flex-col">
        <h2 class="text-2xl font-semibold mb-6">Syncing with Booking.com</h2>

        @if (showProgressMessage) {
          <div class="mt-4 flex flex-col gap-1">
            @for (message of progressMessages; track message; let last = $last) {
              <div class="flex items-center gap-1">
                <p class="m-0 text-gray-600">{{ message }}</p>

                @if (last) {
                  <mat-spinner diameter="16"></mat-spinner>
                }
              </div>
            }
          </div>
        }

        @if (syncErrors) {
          <div class="mt-4 p-4 border border-red-500 rounded-lg">
            <h3 class="text-red-500 font-medium mb-4">{{ syncErrors.message }}</h3>
            @for (error of syncErrors.errors; track error) {
              <p class="text-base m-0 text-gray-600">- {{ error }}</p>
            }

            <p class="text-base text-gray-600">
              We&apos;ve flagged it for our team and an engineer will review it shortly. You&apos;ll hear from us via
              email within 1 business day.
            </p>

            <sbnb-button (click)="onStartSync()" type="primary">Retry</sbnb-button>
          </div>
        }
      </div>
    </ng-container>

    <!-- Success State -->
    <ng-container *ngIf="stage === 'sync_complete'">
      <div class="flex flex-col gap-4">
        <h2 class="text-2xl font-semibold">Success!</h2>
        <p class="m-0">
          Your property has been created on Booking.com. To start getting bookings, please make sure to:
        </p>
        <ul class="list-disc list-inside pl-0 text-base text-gray-600">
          <li>
            Navigate to
            <a
              href="https://admin.booking.com"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sky-500 hover:underline">
              Booking.com extranet
            </a>
            , and request a password reset.
          </li>
          <li>Complete the password reset process.</li>
          <li>Walk through your listing content on Extranet. Read our help article on it.</li>
          <li>Go live with your listing!</li>
        </ul>
      </div>
    </ng-container>

    <!-- Confirm Existing Email -->
    <ng-container *ngIf="stage === 'confirm_existing_email'">
      <div class="flex flex-col gap-4">
        <h2 class="text-2xl font-semibold mb-6">Existing Account Found</h2>
        <p>
          We found an existing Booking.com account associated with the email
          <strong>{{ potentialExistingEntity?.login_email }}</strong>
          .
        </p>
        @if (potentialExistingEntity) {
          <div class="flex flex-col gap-1 p-4 rounded-md bg-slate-100">
            <div class="font-medium">Company name: {{ potentialExistingEntity.company_name }}</div>
            <div class="font-medium">Legal contact name: {{ potentialExistingEntity.legal_contact_name }}</div>
            <div class="text-gray-600">Email: {{ potentialExistingEntity.login_email }}</div>
            <div class="text-gray-600">Has {{ potentialExistingEntity.total_number_of_properties }} properties</div>
          </div>
        }
        <p class="mt-2">Is this your account, and do you want to use it for this property?</p>
      </div>
    </ng-container>

    <!-- Footer -->
    <div class="flex pt-4 mt-6 border-t justify-between items-center">
      <!-- Back Button -->
      <div>
        @if (stage === 'has_account') {
          <sbnb-button (click)="onCancel()" type="secondary">Cancel</sbnb-button>
        } @else if (stage === 'confirm_existing_email') {
          <sbnb-button type="secondary" (click)="onRejectExistingAccount()">No, use a different email</sbnb-button>
        } @else if (stage !== 'sync_complete' && stage !== 'syncing') {
          <sbnb-button (click)="onBack()" type="secondary">Back</sbnb-button>
        }
      </div>

      <!-- Action Buttons -->
      <div class="flex items-center gap-2">
        <!-- Button for Existing Account Email Check -->
        @if (stage === 'existing_account') {
          <sbnb-button type="primary" (click)="checkEmail()" [disabled]="!emailCheckForm.valid || loading">
            @if (loading) {
              <mat-spinner diameter="20"></mat-spinner>
            } @else {
              Connect Account
            }
          </sbnb-button>
        }

        <!-- Button for Create Account Email -->
        @if (stage === 'create_account_email') {
          <sbnb-button type="primary" (click)="onSubmitEmail()" [disabled]="!newAccountEmailForm.valid || loading">
            @if (loading) {
              <mat-spinner diameter="20"></mat-spinner>
            } @else {
              Continue
            }
          </sbnb-button>
        }

        <!-- Button for Entity Details -->
        @if (stage === 'entity_details') {
          <sbnb-button
            type="primary"
            (click)="onSubmitCreateLegalEntity()"
            [disabled]="!legalEntityForm.valid || loading">
            @if (loading) {
              <mat-spinner diameter="20"></mat-spinner>
            } @else {
              Continue
            }
          </sbnb-button>
        }

        @if (stage === 'confirm_existing_email') {
          <sbnb-button type="primary" (click)="onConfirmExistingAccount()">Yes, use this account</sbnb-button>
        }

        @if (stage === 'ready_to_sync') {
          <sbnb-button (click)="onStartSync()" type="primary">Start setup</sbnb-button>
        }

        @if (stage === 'sync_complete') {
          <sbnb-button (click)="onFinished()" type="primary">Done</sbnb-button>
        }

        <!-- Button for Legal Entity ID + Email -->
        @if (stage === 'enter_legal_entity_id_and_email') {
          <sbnb-button type="primary" (click)="onSubmitLegalEntityIdAndEmail()">Continue</sbnb-button>
        }

        <!-- No primary action button needed for entity_type (selection triggers move) -->
        <!-- No primary action button needed for ready_to_sync (button is in main content) -->
      </div>
    </div>
  </div>
</ng-container>
